// assets/delivery-calendar.js
// Hardened build: renders the calendar FIRST (always visible, never blocked by the
// product form lookup), wires add-to-cart lazily, guards against double-include,
// and re-inits on Shopify section load. Keeps prior optimisations (DOM reuse,
// event delegation, cached timestamps, partial updates on click).

(function () {
  'use strict';

  // ── Global guard: if this file is included twice on a page, only boot once ──
  if (window.__PDC_CALENDAR_BOOTED__) return;
  window.__PDC_CALENDAR_BOOTED__ = true;
  window.__PDC_VERSION__ = '2026-09-06 custom-build';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function parseIntOrNull(value) {
    if (value === undefined || value === null || value === '') return null;
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }
  function startOfDay(date) { return new Date(date.getFullYear(), date.getMonth(), date.getDate()); }
  function addDays(date, days) { var d = new Date(date.getFullYear(), date.getMonth(), date.getDate()); d.setDate(d.getDate() + days); return d; }
  function addMonths(date, months) { var d = new Date(date.getFullYear(), date.getMonth(), 1); d.setMonth(d.getMonth() + months); return d; }
  function isWeekend(date) { var day = date.getDay(); return day === 0 || day === 6; }
  function clampDateToMonthRange(date, minDate, maxDate) {
    var d = new Date(date.getFullYear(), date.getMonth(), 1);
    var minM = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    var maxM = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    if (d < minM) return minM; if (d > maxM) return maxM; return d;
  }
  function formatDateForCustomer(date) {
    if (!date) return '';
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return date.getDate() + ' ' + months[date.getMonth()] + ' ' + date.getFullYear();
  }
  function toISODate(date) {
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  }
  function dateFromISO(str) { var p = str.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  function createPdcBookingId() { return 'pdc_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9); }
  function cartUrl(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return root.replace(/\/$/, '') + path;
  }
  /* Money, in the currency the shopper is actually paying in.
     ---------------------------------------------------------------------
     This used to try window.Shopify.formatMoney first. Dawn does not ship
     that function — it belongs to the old option_selection.js — so every
     render fell through to a hardcoded dollar sign and a bare toFixed(2).
     On a Norwegian market the page said "9.356,00 NOK" and this line said
     "$9356.00": right number, wrong currency, wrong separators.

     Intl.NumberFormat is in every browser we support and knows both. The
     currency code comes from Liquid, so it follows the market rather than
     the shop. The old money_format path is kept only as a fallback for a
     theme that really does define Shopify.formatMoney. */
  function pdcCurrency() {
    var c = window.DELIVERY_CALENDAR_CONFIG && window.DELIVERY_CALENDAR_CONFIG.currency;
    if (c) return c;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'NZD';
  }
  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    try {
      return new Intl.NumberFormat(
        (window.Shopify && window.Shopify.locale) || document.documentElement.lang || undefined,
        { style: 'currency', currency: pdcCurrency() }
      ).format(cents / 100);
    } catch (e) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' &&
          window.DELIVERY_CALENDAR_CONFIG && window.DELIVERY_CALENDAR_CONFIG.money_format) {
        return window.Shopify.formatMoney(cents, window.DELIVERY_CALENDAR_CONFIG.money_format);
      }
      return (cents / 100).toFixed(2) + ' ' + pdcCurrency();
    }
  }

  var WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  var MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // ─── Core ───────────────────────────────────────────────────────────────────

  function initCalendar(container) {
    if (container.dataset.pdcInitialized === 'true') return;
    container.dataset.pdcInitialized = 'true';

    // Data attributes
    var basePriceCents            = parseIntOrNull(container.dataset.basePrice);

    /* The price is read from the element once. That was survivable when a
       product had two variants; with sixteen it is not, because Dawn swaps
       the variant WITHOUT firing shopify:section:load, so nothing here would
       ever hear about it. Watch the attribute and the form's variant id, and
       re-read the price when either moves. */
    function pdcRefreshPrice() {
      var next = parseIntOrNull(container.dataset.basePrice);
      if (next === null || next === basePriceCents) return;
      basePriceCents = next;
      updateTexts();
    }
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(pdcRefreshPrice).observe(container, {
        attributes: true, attributeFilter: ['data-base-price']
      });
    }
    document.addEventListener('variant:change', pdcRefreshPrice);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('variant-selects, .product-form__input')) {
        setTimeout(pdcRefreshPrice, 300);   // after Dawn has swapped the markup
      }
    });
    var priorityUpgradeVariantId  = container.dataset.priorityUpgradeVariantId || null;
    var priorityUpgradePriceCents = parseIntOrNull(container.dataset.priorityUpgradePrice) || 0;
    var rushUpgradeVariantId      = container.dataset.rushUpgradeVariantId || null;
    var rushUpgradePriceCents     = parseIntOrNull(container.dataset.rushUpgradePrice) || 0;
    var seaUpgradeVariantId       = container.dataset.seaUpgradeVariantId || null;
    var seaUpgradePriceCents      = parseIntOrNull(container.dataset.seaUpgradePrice) || 0;

    var standardStartOffset = parseIntOrNull(container.dataset.standardStartOffsetDays);
    var standardEndOffset   = parseIntOrNull(container.dataset.standardEndOffsetDays);
    var priorityStartOffset = parseIntOrNull(container.dataset.priorityStartOffsetDays);
    var priorityEndOffset   = parseIntOrNull(container.dataset.priorityEndOffsetDays);
    var rushStartOffset     = parseIntOrNull(container.dataset.rushStartOffsetDays);
    var rushEndOffset       = parseIntOrNull(container.dataset.rushEndOffsetDays);
    var seaStartOffset      = parseIntOrNull(container.dataset.seaStartOffsetDays);
    var seaEndOffset        = parseIntOrNull(container.dataset.seaEndOffsetDays);

    // DOM refs
    var selectedEl   = container.querySelector('.delivery-calendar__selected');
    var windowInfoEl = container.querySelector('.delivery-calendar__window-info');
    var pricingEl    = container.querySelector('.delivery-calendar__pricing');
    var legendEl     = container.querySelector('.delivery-calendar__legend');
    var prevBtn      = container.querySelector('.delivery-calendar__nav--prev');
    var nextBtn      = container.querySelector('.delivery-calendar__nav--next');
    var monthEl      = container.querySelector('.delivery-calendar__month');
    var weekdaysEl   = container.querySelector('.delivery-calendar__weekdays');
    var gridEl       = container.querySelector('.delivery-calendar__grid');

    // Form refs are resolved LATER (lazily) — the calendar must never wait for them.
    var productForm = null;
    var addButton   = null;

    function getCurrentVariantId() {
      var scope = productForm || document;
      var input = scope.querySelector('input[name="id"]');
      if (input && input.value) return input.value;
      var select = scope.querySelector('select[name="id"]');
      if (select && select.value) return select.value;
      return null;
    }

    // Window config.
    // Standard is ALWAYS available (falls back to a default lead time).
    // Priority / Rush / Sea are OPTIONAL: a tier only appears when BOTH its
    // start and end offsets are set on the product. Empty metafield -> no pill.
    var windowsConfig = {
      standard: { key:'standard', label:'Standard', cartLabel:'Standard',
        leadMinDays: standardStartOffset !== null ? standardStartOffset : 41,
        leadMaxDays: standardEndOffset   !== null ? standardEndOffset   : 90, surchargeCents:0, visible:true },
      priority: { key:'priority', label:'Priority', cartLabel:'Priority Build',
        leadMinDays: priorityStartOffset !== null ? priorityStartOffset : 21,
        leadMaxDays: priorityEndOffset   !== null ? priorityEndOffset   : 40, surchargeCents: priorityUpgradePriceCents || 0,
        visible: priorityStartOffset !== null && priorityEndOffset !== null },
      rush: { key:'rush', label:'Rush', cartLabel:'Rush Build',
        leadMinDays: rushStartOffset !== null ? rushStartOffset : 10,
        leadMaxDays: rushEndOffset   !== null ? rushEndOffset   : 20, surchargeCents: rushUpgradePriceCents || 0,
        visible: rushStartOffset !== null && rushEndOffset !== null },
      sea: { key:'sea', label:'Sea', cartLabel:'Sea Freight',
        leadMinDays: seaStartOffset !== null ? seaStartOffset : 21,
        leadMaxDays: seaEndOffset   !== null ? seaEndOffset   : 120, surchargeCents: seaUpgradePriceCents || 0,
        visible: seaStartOffset !== null && seaEndOffset !== null }
    };

    var today   = startOfDay(new Date());
    var todayTs = today.getTime();

    var windowTs = {};
    ['standard', 'priority', 'rush', 'sea'].forEach(function (key) {
      var cfg = windowsConfig[key];
      windowTs[key] = { start: addDays(today, cfg.leadMinDays).getTime(), end: addDays(today, cfg.leadMaxDays).getTime() };
    });

    // Visible date range spans only the tiers that are actually offered.
    var minLead = windowsConfig.standard.leadMinDays;
    var maxLead = windowsConfig.standard.leadMaxDays;
    ['priority', 'rush', 'sea'].forEach(function (k) {
      if (windowsConfig[k].visible) {
        minLead = Math.min(minLead, windowsConfig[k].leadMinDays);
        maxLead = Math.max(maxLead, windowsConfig[k].leadMaxDays);
      }
    });

    var fullMinDate = addDays(today, minLead);
    var fullMaxDate = addDays(today, maxLead);
    var fullMinTs = fullMinDate.getTime(), fullMaxTs = fullMaxDate.getTime();
    var fullMinMonth = new Date(fullMinDate.getFullYear(), fullMinDate.getMonth(), 1).getTime();
    var fullMaxMonth = new Date(fullMaxDate.getFullYear(), fullMaxDate.getMonth(), 1).getTime();

    // ── Made-to-order floor ─────────────────────────────────────────────────
    // A custom build cannot use the stock lead times: the design phase alone is
    // 2-3 weeks. The vehicle selector calls this with a number of days to push
    // the earliest selectable date out, or with null to restore the product's
    // own offsets. Everything downstream is recomputed, so the calendar simply
    // cannot offer a date we could not hit.
    var PDC_KEYS = ['standard', 'priority', 'rush', 'sea'];
    var pdcBase = {};
    PDC_KEYS.forEach(function (k) {
      pdcBase[k] = {
        lead:      windowsConfig[k].leadMinDays,
        leadMax:   windowsConfig[k].leadMaxDays,
        visible:   windowsConfig[k].visible,
        label:     windowsConfig[k].label,
        cartLabel: windowsConfig[k].cartLabel
      };
    });

    container.__pdcSetFloor = function (days, label, until) {
      var floor  = parseIntOrNull(days);
      var custom = floor !== null && floor > 0 && floor < 366;
      var name   = (typeof label === 'string' && label.replace(/\s+/g, '') !== '')
        ? label.trim() : 'Custom build';

      // A floor on its own is not enough. This product's Standard window is
      // 25-30 days wide, so pushing the start to day 56 without moving the end
      // leaves no window at all — which is why the first version simply refused
      // and logged. The made-to-order window is therefore a RANGE: the buyer
      // gets a real span of dates to choose from, starting at the floor.
      var ceiling = parseIntOrNull(until);
      if (custom) {
        var span = pdcBase.standard.leadMax - pdcBase.standard.lead;
        if (!(span > 13)) span = 28;                 // a week is not a choice
        if (ceiling === null || ceiling <= floor) ceiling = floor + span;
      }

      PDC_KEYS.forEach(function (k) {
        var c = windowsConfig[k], b = pdcBase[k];
        // A made-to-order build is one option, not four. Nothing about a custom
        // platform can be hurried — the scan and the approval round happen
        // before manufacturing starts — so the speed tiers are withdrawn rather
        // than sold and then missed, and the one that remains is renamed.
        c.visible     = custom ? (k === 'standard') : b.visible;
        c.label       = (custom && k === 'standard') ? name : b.label;
        c.cartLabel   = (custom && k === 'standard') ? name : b.cartLabel;
        c.leadMinDays = (custom && k === 'standard') ? Math.max(b.lead, floor) : b.lead;
        c.leadMaxDays = (custom && k === 'standard') ? ceiling : b.leadMax;
        windowTs[k] = {
          start: addDays(today, c.leadMinDays).getTime(),
          end:   addDays(today, c.leadMaxDays).getTime()
        };
      });

      // The buyer may have been sitting on Rush when the car changed.
      if (!windowsConfig[selectedWindowKey] || !windowsConfig[selectedWindowKey].visible) {
        selectedWindowKey = 'standard';
      }

      minLead = windowsConfig.standard.leadMinDays;
      maxLead = windowsConfig.standard.leadMaxDays;
      ['priority', 'rush', 'sea'].forEach(function (k) {
        if (windowsConfig[k].visible) {
          minLead = Math.min(minLead, windowsConfig[k].leadMinDays);
          maxLead = Math.max(maxLead, windowsConfig[k].leadMaxDays);
        }
      });

      fullMinDate  = addDays(today, minLead);
      fullMinTs    = fullMinDate.getTime();
      fullMinMonth = new Date(fullMinDate.getFullYear(), fullMinDate.getMonth(), 1).getTime();
      fullMaxDate  = addDays(today, maxLead);
      fullMaxTs    = fullMaxDate.getTime();
      fullMaxMonth = new Date(fullMaxDate.getFullYear(), fullMaxDate.getMonth(), 1).getTime();

      /* Who picked this date — the buyer, or us?

         That question is the whole bug. Releasing a made-to-order floor makes
         the range WIDER, so a date we forced on the buyer is still inside the
         new range and the old "is it still valid?" test happily kept it. On the
         PRO page the Standard window runs 21 to 120 days, so a custom build's
         day-56 date sat comfortably inside it: pick a car we do not stock, pick
         a Model Y again, and the page quoted a stock platform five weeks late
         with "Standard" selected and nothing explaining why.

         Range checks cannot catch that, because the date is genuinely in range.
         The only thing that distinguishes it is provenance. A date the buyer
         clicked is theirs and survives; a date the calendar assigned is ours to
         withdraw the moment the reason for it goes away. */
      var winTs = windowTs[selectedWindowKey];
      var outsideWindow = !winTs || selectedTs < winTs.start || selectedTs > winTs.end;
      var oursToReplace = !pdcDateChosenByBuyer;

      if (!selectedDate || selectedTs < fullMinTs || selectedTs > fullMaxTs ||
          outsideWindow || oursToReplace) {
        selectedDate = getFirstDateForWindow(selectedWindowKey);
        selectedTs   = selectedDate.getTime();
      }
      currentMonth = clampDateToMonthRange(selectedDate, fullMinDate, fullMaxDate);

      buildLegend();        // pills change, so rebuild rather than restyle
      renderCalendar();
      updateTexts();
    };

    function getVisualWindowForTs(ts) {
      if (windowsConfig.rush.visible     && ts >= windowTs.rush.start     && ts <= windowTs.rush.end)     return 'rush';
      if (windowsConfig.priority.visible && ts >= windowTs.priority.start && ts <= windowTs.priority.end) return 'priority';
      if (ts >= windowTs.standard.start && ts <= windowTs.standard.end) return 'standard';
      if (windowsConfig.sea.visible && ts >= windowTs.sea.start && ts <= windowTs.sea.end) return 'sea';
      return null;
    }

    var selectedWindowKey = 'standard';
    var selectedDate = null, selectedTs = null, currentMonth = null;

    /* False while the date is one the calendar chose. Flips to true the moment
       the buyer clicks a day or a speed pill, and from then on their date is
       left alone. Only ever read by __pdcSetFloor. */
    var pdcDateChosenByBuyer = false;

    function getFirstDateForWindow(key) {
      var cfg = windowsConfig[key];
      if (!cfg) return fullMinDate;
      var d = addDays(today, cfg.leadMinDays);
      var endTs = windowTs[key].end;
      while (d.getTime() <= endTs && d.getTime() <= fullMaxTs) {
        if (!isWeekend(d) && getVisualWindowForTs(d.getTime()) === key) return d;
        d = addDays(d, 1);
      }
      return addDays(today, cfg.leadMinDays);
    }

    selectedDate = getFirstDateForWindow('standard');
    selectedTs   = selectedDate.getTime();
    currentMonth = clampDateToMonthRange(selectedDate, fullMinDate, fullMaxDate);

    // Weekdays
    if (weekdaysEl && !weekdaysEl.children.length) {
      var wFrag = document.createDocumentFragment();
      WEEKDAY_NAMES.forEach(function (name) {
        var el = document.createElement('div'); el.className = 'delivery-calendar__weekday'; el.textContent = name; wFrag.appendChild(el);
      });
      weekdaysEl.appendChild(wFrag);
    }

    // Grid — 42 reusable cells
    var cells = [];
    if (gridEl) {
      var gFrag = document.createDocumentFragment();
      for (var ci = 0; ci < 42; ci++) {
        var cell = document.createElement('button'); cell.type = 'button'; cell.className = 'delivery-calendar__day';
        gFrag.appendChild(cell); cells.push(cell);
      }
      gridEl.appendChild(gFrag);
      gridEl.addEventListener('click', function (e) {
        var btn = e.target.closest('.delivery-calendar__day');
        if (!btn || btn.disabled) return;
        var dateStr = btn.dataset.date; if (!dateStr) return;
        var d = dateFromISO(dateStr), ts = d.getTime(), key = getVisualWindowForTs(ts);
        if (!key) return;
        selectedDate = d; selectedTs = ts; selectedWindowKey = key;
        pdcDateChosenByBuyer = true;          // theirs now — stop overriding it
        updateCellSelection(); updateTexts(); updateLegendActiveState();
      });
    }

    function buildLegend() {
      if (!legendEl) return;
      legendEl.innerHTML = '';
      var entries = [ {key:'standard',cls:'is-standard'}, {key:'priority',cls:'is-priority'}, {key:'rush',cls:'is-rush'}, {key:'sea',cls:'is-sea'} ];
      var frag = document.createDocumentFragment();
      entries.forEach(function (entry) {
        var cfg = windowsConfig[entry.key]; if (!cfg || !cfg.visible) return;
        var btn = document.createElement('button'); btn.type = 'button';
        btn.className = 'delivery-calendar__pill ' + entry.cls; btn.textContent = cfg.label;
        btn.setAttribute('data-delivery-window', entry.key);
        btn.addEventListener('click', function () { setSelectedWindow(entry.key, true); });
        frag.appendChild(btn);
      });
      legendEl.appendChild(frag);
      updateLegendActiveState();
    }
    function updateLegendActiveState() {
      if (!legendEl) return;
      legendEl.querySelectorAll('.delivery-calendar__pill').forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-delivery-window') === selectedWindowKey);
      });
    }

    function renderCalendar() {
      if (!gridEl) return;
      var year = currentMonth.getFullYear(), month = currentMonth.getMonth();
      if (monthEl) monthEl.textContent = MONTH_NAMES[month] + ' ' + year;
      var firstWeekday = new Date(year, month, 1).getDay();
      var offset = (firstWeekday + 6) % 7;
      for (var i = 0; i < 42; i++) {
        var cell = cells[i], dayNum = i - offset + 1;
        var cellDate = new Date(year, month, dayNum), cellTs = cellDate.getTime();
        cell.textContent = String(cellDate.getDate());
        cell.dataset.date = toISODate(cellDate);
        cell.disabled = false;
        cell.className = 'delivery-calendar__day';
        var inCurrentMonth = cellDate.getMonth() === month && cellDate.getFullYear() === year;
        if (!inCurrentMonth) cell.classList.add('is-adjacent-month');
        var windowKey = getVisualWindowForTs(cellTs);
        if (windowKey) cell.classList.add('is-window-' + windowKey);
        var weekend = isWeekend(cellDate);
        if (weekend) cell.classList.add('is-weekend');
        var disabled = cellTs < fullMinTs || cellTs > fullMaxTs || !windowKey || weekend;
        if (disabled) { cell.disabled = true; cell.classList.add('is-disabled'); }
        if (cellTs === selectedTs) cell.classList.add('is-selected');
        if (cellTs === todayTs)    cell.classList.add('is-today');
      }
      if (prevBtn) prevBtn.disabled = new Date(year, month - 1, 1).getTime() < fullMinMonth;
      if (nextBtn) nextBtn.disabled = new Date(year, month + 1, 1).getTime() > fullMaxMonth;
    }

    function updateCellSelection() {
      for (var i = 0; i < 42; i++) {
        var cell = cells[i], dateStr = cell.dataset.date; if (!dateStr) continue;
        var isNowSelected = dateFromISO(dateStr).getTime() === selectedTs;
        if (cell.classList.contains('is-selected') !== isNowSelected) cell.classList.toggle('is-selected', isNowSelected);
      }
    }

    function setSelectedWindow(key, moveDateToWindow) {
      if (!windowsConfig[key]) return;
      selectedWindowKey = key;
      if (moveDateToWindow) {
        var newDate = getFirstDateForWindow(key);
        selectedDate = newDate; selectedTs = newDate.getTime();
        pdcDateChosenByBuyer = true;          // they clicked a speed pill
        var newMonthTs = new Date(newDate.getFullYear(), newDate.getMonth(), 1).getTime();
        var curMonthTs = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getTime();
        if (newMonthTs !== curMonthTs) { currentMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1); renderCalendar(); }
        else { updateCellSelection(); }
      } else { updateCellSelection(); }
      updateTexts(); updateLegendActiveState();
    }

    // ------------------------------------------------------------------
    // Mirror the current selection onto the form as real hidden inputs.
    //
    // WHY THIS EXISTS
    //
    // handleAdd() below intercepts the submit and builds the delivery
    // properties itself, so normally nothing needs to be in the DOM. But that
    // interception is not guaranteed: wireCart() gives up after ~3 seconds if
    // it cannot find the form, another script can stop the event first, and on
    // some variants the calendar is not rendered at all. When it does not run,
    // Dawn's own add-to-cart fires instead and the line lands in the cart with
    // NO delivery properties whatsoever.
    //
    // That line can then never be repaired, because pdc-cart-guard only touches
    // items it recognises as delivery items:
    //
    //     isDeliveryItem(props) -> props["Delivery date"] !== undefined
    //                              || props._pdc_delivery_option !== undefined
    //                              || props._pdc_booking_id !== undefined
    //
    // No date in, no date ever. The cart shows no arrival and the reassurance
    // block's third step has nothing to say.
    //
    // These inputs make the DEFAULT survive that path: the calendar already
    // selects the earliest Standard date on init (see the assignment to
    // selectedDate above), so from first paint the form carries it.
    //
    // Safe when interception DOES work: formProperties() reads these back, and
    // handleAdd then overwrites both keys with the same values — its own
    // assignments come last by design.
    // ------------------------------------------------------------------
    function syncDateInputs() {
      var form = productForm || document.querySelector('form[action^="/cart/add"]');
      if (!form || !selectedDate || !selectedWindowKey) return;
      var cfg = windowsConfig[selectedWindowKey] || {};

      function put(key, value) {
        if (!value) return;
        var name = 'properties[' + key + ']';
        var el = form.querySelector('input[name="' + name + '"][data-pdc-sync]');
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = name;
          el.setAttribute('data-pdc-sync', '');
          form.appendChild(el);
        }
        if (el.value !== value) el.value = value;
      }

      put('Delivery date', formatDateForCustomer(selectedDate));
      put('Delivery option', cfg.cartLabel || cfg.label || '');
    }

    function updateTexts() {
      syncDateInputs();
      if (!selectedDate || !selectedWindowKey) {
        if (selectedEl) selectedEl.textContent = 'No date selected yet.';
        if (windowInfoEl) windowInfoEl.innerHTML = '';
        if (pricingEl) pricingEl.textContent = '';
        return;
      }
      var cfg = windowsConfig[selectedWindowKey];
      if (selectedEl) selectedEl.innerHTML = 'Selected date: <strong>' + formatDateForCustomer(selectedDate) + '</strong>';
      if (windowInfoEl) windowInfoEl.innerHTML = '';
      if (!pricingEl || !basePriceCents) return;
      var totalCents = basePriceCents + (cfg.surchargeCents || 0);
      var html = 'Total with <strong>' + cfg.label + '</strong>: <strong>' + formatMoney(totalCents) + '</strong>. ';
      if (cfg.surchargeCents && cfg.surchargeCents > 0) html += 'Includes a <strong>' + formatMoney(cfg.surchargeCents) + '</strong> speed upgrade.';
      else if (selectedWindowKey === 'standard') html += 'No extra production fee applies.';
      pricingEl.innerHTML = html;
    }

    if (prevBtn) prevBtn.addEventListener('click', function () {
      var prev = addMonths(currentMonth, -1);
      if (new Date(prev.getFullYear(), prev.getMonth(), 1).getTime() < fullMinMonth) return;
      currentMonth = prev; renderCalendar();
    });
    if (nextBtn) nextBtn.addEventListener('click', function () {
      var next = addMonths(currentMonth, 1);
      if (new Date(next.getFullYear(), next.getMonth(), 1).getTime() > fullMaxMonth) return;
      currentMonth = next; renderCalendar();
    });

    // ── RENDER FIRST — calendar is now visible, regardless of the product form ──
    buildLegend();
    renderCalendar();
    updateTexts();

    // Dawn REPLACES the whole product form when the variant changes, which
    // throws away the hidden inputs syncDateInputs() just added. Nothing in the
    // calendar fires on a variant change, so without this the date silently
    // stops being attached the moment a buyer switches elevation or drawer —
    // the most common thing they do on these pages.
    //
    // Re-sync after the swap. The listener is on document with capture so it
    // survives the form being replaced, and productForm is re-read each time.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') setTimeout(syncDateInputs, 50);
    }, true);
    document.addEventListener('variant:change', function () {
      setTimeout(syncDateInputs, 50);
    }, true);

    // ─── Cart submission (wired lazily; never blocks the render above) ──────────

    function pushSpeedUpgradeItem(items, selectedKey, variantId, bookingId) {
      var upgradeVariantId = null, windowLabel = '';
      if (selectedKey === 'priority' && priorityUpgradeVariantId) { upgradeVariantId = priorityUpgradeVariantId; windowLabel = 'Priority Build'; }
      else if (selectedKey === 'rush' && rushUpgradeVariantId)    { upgradeVariantId = rushUpgradeVariantId; windowLabel = 'Rush Build'; }
      else if (selectedKey === 'sea' && seaUpgradeVariantId)      { upgradeVariantId = seaUpgradeVariantId; windowLabel = 'Sea Freight'; }
      if (!upgradeVariantId) return;
      items.push({ id: Number(upgradeVariantId), quantity: 1, properties: {
        _pdc_item_type:'production_speed_upgrade', _pdc_booking_id:bookingId, _pdc_upgrade_for:String(variantId),
        _pdc_production_window:selectedKey, _pdc_production_window_label:windowLabel } });
    }

    // Anything else on the form that wants to be a line-item property — the
    // vehicle selector's Car, for one. Without this the payload below is built
    // from scratch and silently drops them.
    function formProperties() {
      var out = {};
      if (!productForm) return out;
      var nodes = productForm.querySelectorAll(
        'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
      for (var i = 0; i < nodes.length; i++) {
        var m = /^properties\[(.+)\]$/.exec(nodes[i].name);
        if (!m) continue;
        var t = (nodes[i].type || '').toLowerCase();
        if ((t === 'checkbox' || t === 'radio') && !nodes[i].checked) continue;
        if (nodes[i].value === '') continue;
        out[m[1]] = nodes[i].value;
      }
      return out;
    }

    var isSubmitting = false;
    function resetSubmitState() {
      isSubmitting = false;
      if (addButton) { addButton.removeAttribute('disabled'); addButton.classList.remove('is-loading'); addButton.setAttribute('aria-disabled', 'false'); }
    }
    function handleAdd(event) {
      if (event) { event.preventDefault(); event.stopPropagation(); if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation(); }
      if (isSubmitting) return false;
      isSubmitting = true;
      if (addButton) { addButton.setAttribute('disabled', 'disabled'); addButton.setAttribute('aria-disabled', 'true'); addButton.classList.add('is-loading'); }
      var variantId = getCurrentVariantId();
      if (!variantId) { alert('Please select a product variant first.'); resetSubmitState(); return false; }
      if (!selectedDate || !selectedWindowKey) {
        alert('Please choose a delivery date before adding to cart.');
        if (container.scrollIntoView) container.scrollIntoView({ behavior:'smooth', block:'center' });
        resetSubmitState(); return false;
      }
      var cfg = windowsConfig[selectedWindowKey];
      var dateISO = toISODate(selectedDate);
      var customerDeliveryDate = formatDateForCustomer(selectedDate);
      var bookingId = createPdcBookingId();
      var standardFallback = getFirstDateForWindow('standard');
      // Customer sees only "Delivery date" + "Delivery option". The 3 underscore-
      // prefixed props are private (hidden from customer) and used by pdc-cart-guard.
      // Form properties first, the calendar's own last, so a delivery value can
      // never be overwritten by something else on the page.
      var props = formProperties();
      props['Delivery date'] = customerDeliveryDate;
      props['Delivery option'] = cfg.cartLabel || cfg.label;
      props._pdc_booking_id = bookingId;
      props._pdc_delivery_option = selectedWindowKey;
      props._pdc_standard_delivery_date_formatted = formatDateForCustomer(standardFallback);
      // Captured from the live section settings so the cart guard can recompute the
      // current free/standard date without hardcoding the offset.
      props._pdc_standard_lead_min_days = String(windowsConfig.standard.leadMinDays);
      var items = [{ id: Number(variantId), quantity: 1, properties: props }];
      pushSpeedUpgradeItem(items, selectedWindowKey, variantId, bookingId);
      fetch(cartUrl('/cart/add.js'), { method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, credentials:'same-origin', body: JSON.stringify({ items: items }) })
        .then(function (res) { if (!res.ok) return res.text().then(function (t) { throw new Error(t || 'Cart request failed'); }); return res.json(); })
        .then(function () { window.location.href = cartUrl('/cart'); })
        .catch(function (err) { console.error(err); alert('Something went wrong while adding this configuration to your cart. Please try again.'); resetSubmitState(); });
      return false;
    }

    // Lazily find the product form + add button. Retries for a few seconds so the
    // calendar works even when the form renders after the calendar or lives elsewhere.
    (function wireCart() {
      var tries = 0;
      (function attempt() {
        var wrapper = container.closest('product-form') || container.closest('form[action^="/cart/add"]') ||
                      document.querySelector('product-form') || document.querySelector('form[action^="/cart/add"]');
        var form = wrapper ? (wrapper.tagName && wrapper.tagName.toLowerCase() === 'form' ? wrapper : wrapper.querySelector('form')) : null;
        var btn = form ? (form.querySelector('button[name="add"]') || form.querySelector('[type="submit"]'))
                       : (wrapper ? (wrapper.querySelector('button[name="add"]') || wrapper.querySelector('[type="submit"]')) : null);
        if (form && btn) {
          productForm = form; addButton = btn;
          if (!form.dataset.pdcCartWired) {
            form.dataset.pdcCartWired = 'true';
            form.addEventListener('submit', handleAdd, true);
            btn.addEventListener('click', handleAdd, true);
          }
          return;
        }
        if (tries++ < 20) setTimeout(attempt, 150); // ~3s of retries
      })();
    })();
  }

  // ─── Boot ───────────────────────────────────────────────────────────────────

  function bootDeliveryCalendars(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var containers = scope.querySelectorAll('.delivery-calendar');
    if (!containers.length) return;
    containers.forEach(initCalendar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootDeliveryCalendars(); }, { once: true });
  } else {
    bootDeliveryCalendars();
  }

  // Re-init when a section is (re)loaded in the theme editor or by dynamic loaders.
  document.addEventListener('shopify:section:load', function (e) {
    bootDeliveryCalendars(e.target || document);
  });
})();